import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import Link from "next/link"
import { Image, ResponsiveImageType } from "react-datocms"
import colors from "@constants/colors"
import { body, bodyBold, h6 } from "@constants/typography"
import { StyledLink } from "@components/buttons/LinkButton"

interface FeaturedProduct {
  id?: string
  productImage?: ResponsiveImageType
  productImageSrc?: string
  productTitle?: string
  subtext?: string
  productTitleColor?: string
  subtextColor?: string
  buttonText?: string
  buttonTextColor?: string
  buttonColor?: string
  buttonLink?: string
}

interface FeaturedSliderProps {
  sectionTitle?: string
  sectionTitleColor?: string
  backgroundColor?: string
  backgroundImageSrc?: string
  featuredProducts?: FeaturedProduct[]
}

const Section = styled.section<{ bgColor?: string; bgImage?: string }>`
  ${({ bgColor, bgImage }) => css`
    width: 100%;
    background-color: ${bgColor ? `#${bgColor}` : "#f4f4f4"};
    background-image: ${bgImage ? `url(${bgImage})` : "none"};
    background-size: cover;
    background-position: center;
  `}
`

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 22px 12px 34px;

  ${up("laptop")} {
    padding: 42px 20px 52px;
  }
`

const Header = styled.h2<{ color?: string }>`
  ${({ color }) => css`
    ${h6}
    margin: 0 auto 18px;
    width: fit-content;
    color: ${color ? `#${color}` : colors.obsidian};
    text-transform: uppercase;
    letter-spacing: 1.6px;
    border-bottom: 2px solid currentColor;
    padding-bottom: 8px;
  `}
`

const Carousel = styled.div`
  position: relative;
`

const ArrowButton = styled.button<{ side: "left" | "right" }>`
  ${({ side }) => css`
    position: absolute;
    top: 50%;
    ${side === "left" ? "left: 0;" : "right: 0;"}
    transform: translateY(-50%);
    z-index: 2;
    width: 42px;
    height: 42px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.94);
    color: #e11a22;
    font-size: 0;
    line-height: 0;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
    display: none;

    ${up("tablet")} {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
`

const ArrowGlyph = styled.span<{ side: "left" | "right" }>`
  ${({ side }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    border-right: 3px solid #e11a22;
    border-bottom: 3px solid #e11a22;
    transform: ${side === "left" ? "rotate(135deg)" : "rotate(-45deg)"};
  `}
`

const Viewport = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: 50%;

  ::-webkit-scrollbar {
    display: none;
  }
`

const Track = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 6px calc(50% - 130px) 10px;

  ${up("tablet")} {
    gap: 20px;
    padding: 8px calc(50% - 160px) 12px;
  }
`

const Item = styled.article<{ active: boolean }>`
  ${({ active }) => css`
    flex: 0 0 260px;
    scroll-snap-align: center;
    text-align: center;
    opacity: ${active ? 1 : 0.48};
    transform: scale(${active ? 1 : 0.9});
    transform-origin: center bottom;
    transition: opacity 220ms ease, transform 220ms ease, filter 220ms ease;
    cursor: ${active ? "default" : "pointer"};

    &:hover {
      opacity: ${active ? 1 : 0.7};
      transform: ${active ? "scale(1)" : "scale(0.94) translateY(-4px)"};
    }

    &:focus-visible {
      opacity: 1;
      transform: scale(0.96) translateY(-4px);
      outline: none;
      filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.16));
    }

    ${up("tablet")} {
      flex-basis: 320px;
    }
  `}
`

const ProductImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 0.78 / 1;

  .DatoImage {
    width: 100%;
    height: 100%;
  }

  .DatoImage img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
`

const ProductImageFallback = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`

const ProductTitle = styled.h3<{ color?: string }>`
  ${({ color }) => css`
    ${bodyBold}
    margin: 12px 0 2px;
    color: ${color ? `#${color}` : colors.obsidian};
    text-transform: uppercase;
    letter-spacing: 0.9px;
    font-size: 28px;
    line-height: 1.05;
  `}
`

const Subtext = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0;
    color: ${color ? `#${color}` : colors.obsidian};
    text-transform: uppercase;
    letter-spacing: 0.7px;
    font-size: 13px;
    line-height: 1.2;
  `}
`

const ActionRow = styled.div`
  margin-top: 12px;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ProductButton = styled(StyledLink)`
  ${css`
    width: auto;
    height: 31px;
    padding: 0 24px;
    border-radius: 999px;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-size: 11px;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const FeaturedSlider: React.FC<FeaturedSliderProps> = ({
  sectionTitle,
  sectionTitleColor,
  backgroundColor,
  backgroundImageSrc,
  featuredProducts = [],
}) => {
  const viewportRef = React.useRef<HTMLDivElement | null>(null)
  const cardRefs = React.useRef<Array<HTMLElement | null>>([])
  const rafRef = React.useRef<number | null>(null)
  const autoScrollRef = React.useRef(false)
  const autoScrollTimerRef = React.useRef<number | null>(null)
  const activeIndexRef = React.useRef(1)
  const [activeIndex, setActiveIndex] = React.useState(1)

  const setAutoScrollState = React.useCallback((enabled: boolean) => {
    autoScrollRef.current = enabled
    if (!enabled) return
    if (autoScrollTimerRef.current != null) {
      window.clearTimeout(autoScrollTimerRef.current)
    }
    autoScrollTimerRef.current = window.setTimeout(() => {
      autoScrollRef.current = false
      autoScrollTimerRef.current = null
    }, 320)
  }, [])

  const goToIndex = React.useCallback(
    (index: number, behavior: ScrollBehavior = "smooth") => {
      const viewport = viewportRef.current
      const target = cardRefs.current[index]
      if (!viewport || !target) return

      const left =
        target.offsetLeft - (viewport.clientWidth - target.clientWidth) / 2
      viewport.scrollTo({ left, behavior })

      activeIndexRef.current = index
      setActiveIndex(index)
      if (behavior === "smooth") {
        setAutoScrollState(true)
      }
    },
    [setAutoScrollState]
  )

  const goPrev = React.useCallback(() => {
    if (featuredProducts.length === 0) return
    if (autoScrollRef.current) return
    const current = activeIndexRef.current
    const isWrap = current - 1 < 0
    const next = isWrap ? featuredProducts.length - 1 : current - 1
    goToIndex(next, isWrap ? "auto" : "smooth")
  }, [featuredProducts.length, goToIndex])

  const goNext = React.useCallback(() => {
    if (featuredProducts.length === 0) return
    if (autoScrollRef.current) return
    const current = activeIndexRef.current
    const isWrap = current + 1 >= featuredProducts.length
    const next = isWrap ? 0 : current + 1
    goToIndex(next, isWrap ? "auto" : "smooth")
  }, [featuredProducts.length, goToIndex])

  React.useEffect(() => {
    if (!featuredProducts.length) return
    const initialIndex = Math.min(1, featuredProducts.length - 1)
    activeIndexRef.current = initialIndex
    setActiveIndex(initialIndex)
    const timer = window.setTimeout(() => goToIndex(initialIndex, "auto"), 80)
    return () => window.clearTimeout(timer)
  }, [featuredProducts.length, goToIndex])

  const onItemSelect = React.useCallback(
    (index: number) => {
      if (index === activeIndexRef.current) return
      goToIndex(index)
    },
    [goToIndex]
  )

  const onScroll = React.useCallback(() => {
    if (rafRef.current != null) return

    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null

      if (autoScrollRef.current) return

      const viewport = viewportRef.current
      if (!viewport) return
      const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2
      let nearestIndex = activeIndexRef.current
      let nearestDistance = Number.POSITIVE_INFINITY

      cardRefs.current.forEach((el, index) => {
        if (!el) return
        const cardCenter = el.offsetLeft + el.clientWidth / 2
        const distance = Math.abs(cardCenter - viewportCenter)
        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestIndex = index
        }
      })

      if (nearestIndex !== activeIndexRef.current) {
        activeIndexRef.current = nearestIndex
        setActiveIndex(nearestIndex)
      }
    })
  }, [])

  React.useEffect(
    () => () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current)
      }
      if (autoScrollTimerRef.current != null) {
        window.clearTimeout(autoScrollTimerRef.current)
      }
    },
    []
  )

  return (
    <Section bgColor={backgroundColor} bgImage={backgroundImageSrc}>
      <Container>
        {sectionTitle && (
          <Header color={sectionTitleColor}>{sectionTitle}</Header>
        )}

        <Carousel>
          {featuredProducts.length > 1 && (
            <>
              <ArrowButton side="left" onClick={goPrev} aria-label="Previous">
                <ArrowGlyph side="left" />
              </ArrowButton>
              <ArrowButton side="right" onClick={goNext} aria-label="Next">
                <ArrowGlyph side="right" />
              </ArrowButton>
            </>
          )}

          <Viewport ref={viewportRef} onScroll={onScroll}>
            <Track>
              {featuredProducts.map((product, index) => {
                return (
                  <Item
                    key={
                      product.id ||
                      `${product.productTitle || "product"}-${index}`
                    }
                    active={index === activeIndex}
                    role={index === activeIndex ? undefined : "button"}
                    tabIndex={index === activeIndex ? -1 : 0}
                    aria-pressed={index === activeIndex ? undefined : false}
                    onClick={
                      index === activeIndex
                        ? undefined
                        : () => onItemSelect(index)
                    }
                    onKeyDown={(event) => {
                      if (index === activeIndex) return
                      if (event.key !== "Enter" && event.key !== " ") return
                      event.preventDefault()
                      onItemSelect(index)
                    }}
                    ref={(node) => {
                      cardRefs.current[index] = node
                    }}
                  >
                    <ProductImageWrap>
                      {product.productImage ? (
                        <Image
                          data={product.productImage}
                          className="DatoImage"
                        />
                      ) : product.productImageSrc ? (
                        <ProductImageFallback
                          src={product.productImageSrc}
                          alt=""
                        />
                      ) : null}
                    </ProductImageWrap>
                    {product.productTitle && (
                      <ProductTitle color={product.productTitleColor}>
                        {product.productTitle}
                      </ProductTitle>
                    )}
                    {product.subtext && (
                      <Subtext color={product.subtextColor}>
                        {product.subtext}
                      </Subtext>
                    )}
                    <ActionRow>
                      {index === activeIndex &&
                        product.buttonText &&
                        product.buttonLink && (
                          <Link href={product.buttonLink} passHref>
                            <ProductButton
                              $buttonColor={product.buttonColor}
                              $buttonTextColor={product.buttonTextColor}
                            >
                              {product.buttonText}
                            </ProductButton>
                          </Link>
                        )}
                    </ActionRow>
                  </Item>
                )
              })}
            </Track>
          </Viewport>
        </Carousel>
      </Container>
    </Section>
  )
}

export default FeaturedSlider
